<script setup lang="ts">
  import { ref, computed, onMounted, onUnmounted } from "vue";
  import { useRouter } from "vue-router";
  import { storeToRefs } from "pinia";
  import moment from "moment";
  import { Trash2, Pencil, Download, Upload, Plus, Presentation } from "lucide-vue-next";
  import AiStar from "@/components/episto-ui/ai/AiStar.vue";
  import { IndexPageLayout, IndexPageFilters } from "@/components/App/IndexPage";
  import { Button } from "@/components/ui/button";
  import { FilterDropdown } from "@/components/episto-ui/filter-dropdown";
  import { Tooltip } from "@/components/ui/tooltip";
  import {
    DataTable,
    DataTableActions,
    DataTableColumnPicker,
    type DataTableColumn,
    type DataTableAction,
    type ToggleableColumn,
  } from "@/components/episto-ui/data-table";
  import { ConfirmDialog } from "@/components/episto-ui/confirm-dialog";
  import { useConfirmDelete } from "@/utils/composables/useConfirmDelete";
  import JcdCampaignImportDialog from "./JcdCampaignImportDialog.vue";
  import JcdGenerateSurveyDialog from "./JcdGenerateSurveyDialog.vue";
  import { useJcdCampaignsStore, PAGE_SIZE } from "@stores/jcdCampaigns";
  import { useToastsStore } from "@stores/toastsStore";
  import { useJcdCampaignsFilters } from "@utils/composables/filters/useJcdCampaignsFilters";
  import type { JcdCampaign } from "@/types";

  const router = useRouter();
  const store = useJcdCampaignsStore();
  const toast = useToastsStore();
  const { campaigns, campaignsCount, currentPage, searchQuery, isLoading } = storeToRefs(store);

  const { filterConfigs, activeFiltersCount, handleFilterChange, handleClear } = useJcdCampaignsFilters();

  onMounted(() => {
    store.fetchReferentials();
    store.fetch(1);
  });
  onUnmounted(() => store.resetAllFilters());

  const totalPages = computed(() => Math.ceil(campaignsCount.value / PAGE_SIZE) || 1);

  const formatPct = (value: number | null) => (value == null ? "—" : `${Math.round(value * 100)}%`);
  const formatDate = (date: string | null) => (date ? moment(date).format("DD/MM/YYYY") : "—");
  const formatList = (values: string[]) => (values.length ? values.join(", ") : "—");

  const TOGGLEABLE_COLUMNS: ToggleableColumn[] = [
    { key: "logo_url", label: "Logo" },
    { key: "brand", label: "Marque" },
    { key: "week", label: "Semaine" },
    { key: "year", label: "Année" },
    { key: "starts_on", label: "Début", hiddenByDefault: true },
    { key: "ends_on", label: "Fin", hiddenByDefault: true },
    { key: "family", label: "Famille", hiddenByDefault: true },
    { key: "family_class", label: "Classe", hiddenByDefault: true },
    { key: "support", label: "Support" },
    { key: "gross_budget", label: "Budget", hiddenByDefault: true },
    { key: "faces_count", label: "Nb faces", hiddenByDefault: true },
    { key: "networks", label: "Réseau", hiddenByDefault: true },
    { key: "visuals_count", label: "Nb visuels", hiddenByDefault: true },
    { key: "formats", label: "Format", hiddenByDefault: true },
    { key: "mosaic_code", label: "Code Mosaic", hiddenByDefault: true },
    { key: "level", label: "Niveau", hiddenByDefault: true },
    { key: "reco", label: "Reco" },
    { key: "reco_benchmark", label: "Reco bench.", hiddenByDefault: true },
    { key: "attribution", label: "Attribution" },
    { key: "attribution_benchmark", label: "Attrib. bench.", hiddenByDefault: true },
    { key: "confusion", label: "Confusion", hiddenByDefault: true },
    { key: "nsp", label: "NSP", hiddenByDefault: true },
    { key: "intention", label: "Intention" },
    { key: "intention_benchmark", label: "Intention bench.", hiddenByDefault: true },
    { key: "questionnaire_ready", label: "Statut" },
  ];

  const columnVisibility = ref<Record<string, boolean>>({});
  const isColumnVisible = (column: ToggleableColumn) => columnVisibility.value[column.key] ?? !column.hiddenByDefault;

  const allColumns = computed<DataTableColumn<JcdCampaign>[]>(() => [
    { key: "logo_url", label: "", width: 48 },
    { key: "brand", label: "Marque", class: "font-medium" },
    { key: "week", label: "Semaine", width: 90, render: (item) => `S${item.week}` },
    { key: "year", label: "Année", width: 80, render: (item) => item.year.toString() },
    { key: "starts_on", label: "Début", width: 100, render: (item) => formatDate(item.starts_on) },
    { key: "ends_on", label: "Fin", width: 100, render: (item) => formatDate(item.ends_on) },
    { key: "family", label: "Famille", render: (item) => item.family || "—" },
    { key: "family_class", label: "Classe", render: (item) => item.family_class || "—" },
    { key: "support", label: "Support", width: 100, render: (item) => item.support || "—" },
    {
      key: "gross_budget",
      label: "Budget",
      width: 110,
      render: (item) => (item.gross_budget == null ? "—" : `${item.gross_budget.toLocaleString("fr-FR")} €`),
    },
    { key: "faces_count", label: "Nb faces", width: 90, render: (item) => item.faces_count?.toString() || "—" },
    { key: "networks", label: "Réseau", render: (item) => formatList(item.networks) },
    { key: "visuals_count", label: "Nb visuels", width: 100, render: (item) => item.visuals_count?.toString() || "—" },
    { key: "formats", label: "Format", render: (item) => formatList(item.formats) },
    { key: "mosaic_code", label: "Code Mosaic", width: 110, render: (item) => item.mosaic_code || "—" },
    { key: "level", label: "Niveau", width: 100, render: (item) => item.level || "—" },
    { key: "reco", label: "Reco", width: 80, render: (item) => formatPct(item.reco) },
    { key: "reco_benchmark", label: "Reco bench.", width: 105, render: (item) => formatPct(item.reco_benchmark_effective) },
    { key: "attribution", label: "Attribution", width: 100, render: (item) => formatPct(item.attribution) },
    {
      key: "attribution_benchmark",
      label: "Attrib. bench.",
      width: 110,
      render: (item) => formatPct(item.attribution_benchmark_effective),
    },
    { key: "confusion", label: "Confusion", width: 95, render: (item) => formatPct(item.confusion) },
    { key: "nsp", label: "NSP", width: 80, render: (item) => formatPct(item.nsp) },
    { key: "intention", label: "Intention", width: 95, render: (item) => formatPct(item.intention) },
    {
      key: "intention_benchmark",
      label: "Intention bench.",
      width: 125,
      render: (item) => formatPct(item.intention_benchmark_effective),
    },
    { key: "questionnaire_ready", label: "Statut", width: 70 },
  ]);

  const columns = computed(() =>
    allColumns.value.filter((column) => {
      const toggleable = TOGGLEABLE_COLUMNS.find(({ key }) => key === column.key);
      return !toggleable || isColumnVisible(toggleable);
    }),
  );

  const tableActions: DataTableAction[] = [{ key: "delete", label: "Supprimer", icon: Trash2, variant: "destructive" }];

  const isImportDialogOpen = ref(false);
  const isGenerateDialogOpen = ref(false);

  const {
    isOpen: isDeleteAlertOpen,
    target: campaignToDelete,
    requestDelete,
    confirm: handleDelete,
  } = useConfirmDelete<JcdCampaign>(async (campaign) => {
    try {
      await store.destroy(campaign.id);
      toast.success("Campagne supprimée");
    } catch {
      toast.error("La suppression a échoué");
    }
  });

  const handleAction = (action: string, campaign: JcdCampaign) => {
    if (action === "delete") {
      requestDelete(campaign);
    }
  };
</script>

<template>
  <IndexPageLayout
    title="Campagnes JCDecaux"
    padding="none">
    <template #actions>
      <Button
        variant="ghost"
        class="text-muted-foreground"
        @click="store.exportCampaigns()">
        <Download />
        Exporter
      </Button>
      <Button
        variant="ghost"
        class="text-muted-foreground"
        @click="isImportDialogOpen = true">
        <Upload />
        Importer
      </Button>
      <Button @click="isGenerateDialogOpen = true">
        <AiStar class="mr-2 h-4 w-4" />
        Nouveau questionnaire
      </Button>
      <Button @click="router.push('/jcd-campaigns/new')">
        <Plus class="mr-2 h-4 w-4" />
        Nouvelle campagne
      </Button>
    </template>
    <template #filters>
      <div class="space-y-4">
        <IndexPageFilters
          v-model:search-model-value="searchQuery"
          search-placeholder="Rechercher une marque...">
          <template #filters>
            <FilterDropdown
              :filters="filterConfigs"
              :active-filters-count="activeFiltersCount"
              @toggle="handleFilterChange"
              @clear="handleClear" />
            <DataTableColumnPicker
              v-model="columnVisibility"
              :columns="TOGGLEABLE_COLUMNS" />
          </template>
        </IndexPageFilters>
      </div>
    </template>

    <DataTable
      :items="campaigns"
      :columns="columns"
      :current-page="currentPage"
      :total-pages="totalPages"
      :loading="isLoading"
      variant="compact"
      height="calc(100vh - 280px)"
      empty-message="Aucune campagne."
      @page-change="(page) => store.fetch(page)">
      <template #cell-logo_url="{ item }">
        <img
          v-if="item.logo_url"
          :src="item.logo_url"
          :alt="item.brand"
          class="h-8 w-8 rounded bg-white object-contain p-0.5" />
        <div
          v-else
          class="bg-muted h-8 w-8 rounded" />
      </template>
      <template #cell-questionnaire_ready="{ item }">
        <Tooltip
          v-if="!item.questionnaire_ready"
          content="Informations manquantes pour créer le questionnaire">
          <span class="inline-flex">⚠️</span>
        </Tooltip>
        <span v-else />
      </template>
      <template #actions="{ item }">
        <div class="flex items-center justify-end">
          <Tooltip
            v-if="item.survey_id"
            content="Le rapport sera téléchargeable une fois la collecte terminée"
            :disabled="item.survey_status === 'complete'">
            <span class="inline-flex">
              <Button
                variant="ghost"
                size="sm"
                :disabled="item.survey_status !== 'complete' || item.reco == null"
                @click="store.generatePpt(item.id)">
                <Presentation class="h-4 w-4" />
                <span class="pl-1 text-sm font-semibold">Rapport</span>
              </Button>
            </span>
          </Tooltip>
          <RouterLink :to="`/jcd-campaigns/${item.id}`">
            <Button
              variant="ghost"
              size="sm">
              <Pencil class="h-4 w-4" />
              <span class="pl-1 text-sm font-semibold">Modifier</span>
            </Button>
          </RouterLink>
          <DataTableActions
            :actions="tableActions"
            @action="(action) => handleAction(action, item)" />
        </div>
      </template>
    </DataTable>

    <template #dialogs>
      <JcdCampaignImportDialog v-model:open="isImportDialogOpen" />
      <JcdGenerateSurveyDialog v-model:open="isGenerateDialogOpen" />
      <ConfirmDialog
        v-model:open="isDeleteAlertOpen"
        title="Supprimer la campagne ?"
        confirm-label="Supprimer"
        cancel-label="Annuler"
        @confirm="handleDelete">
        Cette action est irréversible. La campagne
        <span class="font-bold">{{ campaignToDelete?.brand }}</span>
        et toutes ses données seront définitivement supprimées.
      </ConfirmDialog>
    </template>
  </IndexPageLayout>
</template>
